import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ACTION_CATALOG } from "@/lib/midi/presets";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * BindLastMidiPill — floating Studio pill that appears when a MIDI signal
 * with no binding arrives. One tap opens a compact modal to pick deck +
 * action and persist the binding. Zero Settings trip.
 */
export default function BindLastMidiPill({ midi }) {
  const [open, setOpen] = useState(false);
  const [deck, setDeck] = useState(0);
  const [action, setAction] = useState("play");

  if (!midi?.lastUnmapped) return null;

  const submit = () => {
    midi.bindSignature(midi.lastUnmapped.sig, { deck, action });
    toast.success(`Bound to Deck ${["A","B","C","D"][deck]} · ${ACTION_CATALOG.find(a => a.id === action)?.label || action}`);
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        data-testid="midi-bind-pill"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 px-4 py-2.5 rounded-full bg-[#00F0FF] text-black font-black uppercase tracking-widest text-xs flex items-center gap-2 shadow-[0_10px_30px_rgba(0,240,255,0.35)] hover:bg-[#33F3FF] transition-colors animate-pulse"
        title={`Unmapped MIDI signal: ${midi.lastUnmapped.sig}`}
      >
        <Sparkles className="w-3.5 h-3.5"/>
        Bind last signal
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="midi-bind-modal" className="bg-[#121620] border-[#00F0FF]/60 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl">Bind MIDI signal</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#94A3B8]">
            Detected an unmapped signal from your controller.
            Pick the deck + action it should trigger — saved automatically for next time.
          </p>
          <div className="text-[11px] font-mono bg-[#0B0E14] border border-[#1E2638] rounded px-3 py-2" data-testid="midi-bind-signature">
            <span className="text-[#94A3B8]">Signature:</span> <span className="text-[#00F0FF]">{midi.lastUnmapped.sig}</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-widest text-[#94A3B8]">Deck</Label>
              <div className="grid grid-cols-4 gap-1" data-testid="midi-bind-deck">
                {["A","B","C","D"].map((letter, i) => (
                  <button key={i} type="button" onClick={() => setDeck(i)}
                    className={`py-1 rounded text-xs font-black transition-colors ${deck === i ? "bg-[#00F0FF] text-black" : "bg-[#0B0E14] text-[#94A3B8] border border-[#1E2638] hover:text-white"}`}>
                    {letter}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-widest text-[#94A3B8]">Action</Label>
              <select value={action} onChange={e => setAction(e.target.value)} data-testid="midi-bind-action"
                className="w-full bg-[#0B0E14] border border-[#1E2638] rounded px-2 py-1.5 text-xs text-white focus:border-[#00F0FF] focus:outline-none">
                {ACTION_CATALOG.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button data-testid="midi-bind-submit" onClick={submit} className="flex-1 bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-black uppercase tracking-widest">Bind</Button>
            <Button data-testid="midi-bind-dismiss" variant="outline" onClick={() => { midi.dismissUnmapped(); setOpen(false); }}
                    className="border-[#2A354B] bg-transparent text-white hover:bg-[#1E2638] font-black uppercase tracking-widest">Dismiss</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
